import React, { useMemo } from 'react';
import {
  TrendingDown,
  Users,
  Building2,
  AlertTriangle,
  UserCheck,
} from 'lucide-react';
import {
  TrendLineChart,
  DonutChart,
  SparklineCard,
  RankingTable,
  RegionHeatmap,
} from '../../../components/charts';
import type { RankingEntry, RegionTrendData } from '../../../types/analytics';
import { generateSparklineFromTimeSeries } from '../useAnalyticsData';

interface OverviewTabProps {
  metrics: {
    totalEmployees: number;
    overallAttendance: number;
    regularAttendance: number;
    contractualAttendance: number;
  } | null;
  dailyAttendanceSummary: any;
  totalKVISData: any;
  topRegionTrends: RegionTrendData[];
  heatmapData: any[];
  topRegions: RankingEntry[];
  bottomRegions: RankingEntry[];
  employeeApiType: 'regular' | 'contractual';
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  metrics,
  dailyAttendanceSummary,
  totalKVISData,
  topRegionTrends,
  heatmapData,
  topRegions,
  bottomRegions,
  employeeApiType,
}) => {
  // Attendance distribution data for donut chart
  const attendanceDistribution = useMemo(() => {
    if (!metrics) return [];
    const totalPresent = Math.round((metrics.totalEmployees * metrics.overallAttendance) / 100);
    const totalAbsent = Math.round(metrics.totalEmployees * ((100 - metrics.overallAttendance) / 100) * 0.6);
    const totalNoResponse = metrics.totalEmployees - totalPresent - totalAbsent;

    return [
      { name: 'Present', value: totalPresent, color: '#10b981' },
      { name: 'Absent', value: totalAbsent, color: '#ef4444' },
      { name: 'No Response', value: totalNoResponse, color: '#f59e0b' },
    ];
  }, [metrics]);

  // Generate consistent sparkline data from top region trends
  const sparklineData = useMemo(() => {
    const ts = topRegionTrends[0]?.timeSeries;
    return {
      regular: generateSparklineFromTimeSeries(ts, 14, 'regularPercent'),
      contractual: generateSparklineFromTimeSeries(ts, 14, 'contractualPercent'),
    };
  }, [topRegionTrends]);

  const heatmapDisplayData = useMemo(
    () =>
      heatmapData.map(d => ({
        ...d,
        value: employeeApiType === 'contractual' ? d.contractualPercent : d.regularPercent,
      })),
    [heatmapData, employeeApiType],
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SparklineCard
          title="Regular Staff"
          value={`${
            dailyAttendanceSummary?.data?.[0]?.presentPercentageReg
              ? parseFloat(dailyAttendanceSummary.data[0].presentPercentageReg).toFixed(2)
              : metrics?.regularAttendance || 0
          }%`}
          subtitle={
            dailyAttendanceSummary?.data?.[0]?.totalEnrollmentReg
              ? `${parseInt(dailyAttendanceSummary.data[0].totalEnrollmentReg).toLocaleString()} enrolled`
              : undefined
          }
          data={sparklineData.regular}
          trend="up"
          trendValue={2.3}
          color="#10b981"
          icon={<UserCheck className="w-5 h-5" />}
        />
        <SparklineCard
          title="Contractual Staff"
          value={`${
            dailyAttendanceSummary?.data?.[0]?.presentPercentageCon
              ? parseFloat(dailyAttendanceSummary.data[0].presentPercentageCon).toFixed(2)
              : metrics?.contractualAttendance || 0
          }%`}
          subtitle={
            dailyAttendanceSummary?.data?.[0]?.totalEnrollmentCon
              ? `${parseInt(dailyAttendanceSummary.data[0].totalEnrollmentCon).toLocaleString()} enrolled`
              : undefined
          }
          data={sparklineData.contractual}
          trend="stable"
          trendValue={0.5}
          color="#f59e0b"
          icon={<Users className="w-5 h-5" />}
        />
        <SparklineCard
          title="Schools"
          value={totalKVISData?.data?.schoolCount || 0}
          subtitle={`${totalKVISData?.data?.kvCount || 0} KVs total`}
          data={Array.from({ length: 14 }, () => totalKVISData?.data?.schoolCount || 0)}
          trend="stable"
          trendValue={0}
          color="#3b82f6"
          icon={<Building2 className="w-5 h-5" />}
        />
        <SparklineCard
          title="Shift Schools"
          value={
            totalKVISData?.data?.kvCount && totalKVISData?.data?.schoolCount
              ? totalKVISData.data.kvCount - totalKVISData.data.schoolCount
              : 0
          }
          subtitle={`${totalKVISData?.data?.schoolCount || 0} single shift`}
          data={Array.from(
            { length: 14 },
            () =>
              totalKVISData?.data?.kvCount && totalKVISData?.data?.schoolCount
                ? totalKVISData.data.kvCount - totalKVISData.data.schoolCount
                : 0,
          )}
          trend="stable"
          trendValue={0}
          color="#8b5cf6"
          icon={<Building2 className="w-5 h-5" />}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Line Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Attendance Trend (Top 5 Regions)</h3>
            <span className="text-sm text-purple-600 font-medium">
              {employeeApiType === 'regular' ? 'Regular Staff' : 'Contractual Staff'}
            </span>
          </div>
          <TrendLineChart
            data={topRegionTrends}
            height={320}
            showLegend={true}
            showArea={true}
            compareMode={employeeApiType}
          />
        </div>

        {/* Distribution Donut */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Today's Distribution</h3>
          <DonutChart
            data={attendanceDistribution}
            height={300}
            centerLabel="Total"
            centerValue={metrics?.totalEmployees?.toLocaleString()}
            showLegend={true}
          />
        </div>
      </div>

      {/* Heatmap and Rankings Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* State Heatmap */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Attendance by Region</h3>
            <span className="text-sm text-purple-600 font-medium">
              {employeeApiType === 'regular' ? 'Regular Staff' : 'Contractual Staff'}
            </span>
          </div>
          <RegionHeatmap data={heatmapDisplayData} height={500} showLabels={true} />
        </div>

        {/* Top/Bottom Performers */}
        <div className="space-y-4">
          <RankingTable
            data={topRegions}
            title="🏆 Top Performing Regions"
            limit={5}
            highlightTop={3}
            showRankChange={true}
          />
          <RankingTable
            data={bottomRegions}
            title="⚠️ Needs Attention"
            limit={5}
            highlightBottom={3}
            showRankChange={true}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(OverviewTab);
